/**
 * Request/response helpers aligned with ots_be DTOs (services/dto/create-service.dto.ts).
 */

export function toApiString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number' && !Number.isNaN(value)) return String(value);
  const s = String(value).trim();
  return s === '' ? fallback : s;
}

export function isDeployedApiError(body: unknown): body is { message?: string | string[]; success?: boolean; statusCode?: number } {
  if (!body || typeof body !== 'object') return false;
  const o = body as { success?: boolean; statusCode?: number };
  return o.success === false || (typeof o.statusCode === 'number' && o.statusCode >= 400);
}

export function getDeployedApiErrorMessage(body: unknown, fallback: string): string {
  if (!isDeployedApiError(body)) return fallback;
  const m = (body as { message?: string | string[] }).message;
  if (Array.isArray(m)) return m.join(', ');
  if (typeof m === 'string' && m.trim()) return m;
  return fallback;
}

export function assertDeployedApiSuccess(body: unknown, fallback: string): void {
  if (isDeployedApiError(body)) {
    throw new Error(getDeployedApiErrorMessage(body, fallback));
  }
}

export function extractDeployedList<T>(body: unknown): T[] {
  if (!body || typeof body !== 'object') return [];
  const o = body as { data?: unknown };
  const raw = o.data ?? body;
  return Array.isArray(raw) ? (raw as T[]) : [];
}

/** Admin catalog lists — full data, not area_city filtered (deployed OpenAPI). */
export const ADMIN_CATEGORIES_LIST_ENDPOINT = '/services-catageory/findAllServiceCatageory';
export const ADMIN_SERVICES_LIST_ENDPOINT = '/services/fetchAllServices';

/** Customer catalog — area_city required on deployed API (location table). */
export const CUSTOMER_CATEGORIES_ENDPOINT = '/services-catageory';
export const CUSTOMER_SERVICES_ENDPOINT = '/services';

/**
 * POST /services — CreateServiceDto (ots_be).
 * Required: service_name, service_about, service_catageory_id.
 * Pricing fields must be strings (typo: serivce_pricing).
 */
export function buildCreateServiceBody(values: Record<string, unknown>) {
  const price = toApiString(values.serivce_pricing, '0');
  return {
    service_name: toApiString(values.service_name ?? values.name),
    service_about: toApiString(values.service_about),
    serivce_pricing: price,
    service_catageory_id: toApiString(values.service_catageory_id),
    base_pricing: toApiString(values.base_pricing, price),
    discount_pricing: toApiString(values.discount_pricing, price),
    gst_percentage: toApiString(values.gst_percentage, '0'),
    payout_percentage: toApiString(values.payout_percentage, '0'),
  };
}

/**
 * PATCH /services/:id — UpdateServiceDetailsDto (ots_be).
 * All fields optional; every value must be a string.
 * Include service_catageory_id when changing category (ots_be reads it after whitelist).
 */
export function buildUpdateServiceBody(
  values: Record<string, unknown>,
  existing?: Record<string, unknown>
) {
  const price = toApiString(
    values.serivce_pricing ?? existing?.serivce_pricing ?? existing?.base_pricing,
    '0'
  );
  const categoryId = toApiString(
    values.service_catageory_id ??
      existing?.service_catageory_id ??
      (existing?.service_catageory as { id?: string } | undefined)?.id
  );

  const body: Record<string, string> = {
    service_name: toApiString(values.service_name ?? values.name ?? existing?.service_name, ''),
    service_about: toApiString(values.service_about ?? existing?.service_about, ''),
    serivce_pricing: price,
    base_pricing: toApiString(values.base_pricing ?? existing?.base_pricing, price),
    discount_pricing: toApiString(values.discount_pricing ?? existing?.discount_pricing, price),
    gst_percentage: toApiString(values.gst_percentage ?? existing?.gst_percentage, '0'),
    payout_percentage: toApiString(values.payout_percentage ?? existing?.payout_percentage, '0'),
  };

  if (categoryId) {
    body.service_catageory_id = categoryId;
  }

  return body;
}

/** POST /services-catageory — CreateServicesCatageoryDto */
export function buildCreateServiceCategoryBody(values: Record<string, unknown>) {
  return {
    service_catageory_name: toApiString(values.service_catageory_name ?? values.name),
    service_catageory_about: toApiString(values.service_catageory_about),
    service_catageory_description: toApiString(values.service_catageory_description),
  };
}

/** PATCH /services-catageory/:id */
export function buildUpdateServiceCategoryBody(values: Record<string, unknown>) {
  return buildCreateServiceCategoryBody(values);
}
